import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CreditCardSetupInitiatorDialog } from './CreditCardSetupInitiatorDialog';
import { useDisclosure, useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import type { NormalizableGraphError } from '@shared/utils/graphError';
import type { ShippingAddressFragment } from '@graphql/fragments/shippingAddress.graphql.generated';
import { EventUserStatus, RegistryPayoutAccountWithdrawalStatus } from '@graphql/generated-base';
import type { ExternalAccount } from '@shared/components/PayoutDialog/PayoutDialog.types';
import { useCreateRegistryPayoutAccountMutation } from '@graphql/mutations/registry.graphql.generated';
import { useSetRegistryCurrencyMutation } from '@graphql/mutations/currency.graphql.generated';
import { useGetPayoutAccountInfoLazyQuery } from '@graphql/queries/registry.graphql.generated';
import { useGetPayoutAccountOnboardingUrlLazyQuery, useInitCashFundsHostFromRegistryMutation } from './CreditCardSetup.graphql.generated';
import { useAuth } from '@shared/components/AuthProvider';
import { normalizeGraphError, useTranslateGraphError, errorFromGraphError } from '@shared/utils/graphError';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import type { PayoutAccountInfo } from './CreditCardSetUp.context';
import { Provider } from './CreditCardSetUp.context';
import { withWindow } from '@shared/utils/withWindow';
import { PayoutDialog } from './PayoutDialog/PayoutDialog';
import { usePayoutDialogController } from './PayoutDialog/PayoutDialog.controller';
import { isAddressValid, isUSAddress } from '@apps/registry/common/utils/shippingAddressValidations';
import { usePreviousValue } from '@shared/utils/hooks/usePreviousValue';
import { hasActiveStripeAccount } from '@shared/utils/payoutAccount';
import { canWithdrawFunds, isCashEquivalentSupported } from './CreditCardSetup.utils';
import { usePostStripeReturnEffect } from './useStripeReturnEffects';
import { useCreditCardSetupTelemetry } from './CreditCardSetUp.telemetry';
import { useAdminRegistryTelemetry } from '../../AdminRegistry.telemetry';
import { addError } from '@shared/utils/logger';
import { useResolvedCashFundLocale } from '@shared/utils/currency/useResolvedCashFundLocale';

const CHECK_STRIPE_STATUS_PARAM = 'check_stripe_status';

const mapRegistryExternalAccount = (
  account: { __typename?: string; bankName?: string | null; brand?: string; last4: string; availablePayoutMethods: ReadonlyArray<string> } | null | undefined,
): ExternalAccount | null => {
  if (!account) return null;
  return {
    institutionName: account.__typename === 'RegistryPayoutAccountBankExternalAccount' ? (account.bankName ?? null) : (account.brand ?? null),
    last4: account.last4,
    availablePayoutMethods: account.availablePayoutMethods as ReadonlyArray<'instant' | 'standard'>,
  };
};

const appendStripeStatusMarker = (route: string): string => {
  const url = new URL(route, 'https://placeholder.invalid');
  url.searchParams.set(CHECK_STRIPE_STATUS_PARAM, 'true');
  return `${url.pathname}${url.search}${url.hash}`;
};

// we're playing with this until we figure out the right amount of attempts and time.
const MAX_ATTEMPTS = 4;

export type CreditCardSetupProviderProps = Readonly<{
  children: React.ReactNode;
  eventId: string;
  registryShippingAddress: Readonly<ShippingAddressFragment> | null;
  handleInvalidShippingAddress?: (firstName?: string, lastName?: string) => void;
  handleChangeCurrency?: () => void;
  registryCurrencyCode?: string;
}>;

const CreditCardSetupProvider: React.FC<CreditCardSetupProviderProps> = ({
  children,
  eventId,
  registryShippingAddress,
  handleInvalidShippingAddress,
  handleChangeCurrency,
  registryCurrencyCode,
}) => {
  const openWindow = useWindowFunction('open');
  const {
    currentUser: { activeEventUser },
    isSuperAdmin,
  } = useAuth();
  const { toast } = useToast();
  const { translateGraphErrorCodeOrUnknown } = useTranslateGraphError();
  const telemetry = useCreditCardSetupTelemetry();
  const registryTelemetry = useAdminRegistryTelemetry();
  const [payoutAccountInfo, setPayoutAccountInfo] = useState<PayoutAccountInfo | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [hasKYCFailed, setHasKYCFailed] = useState(false);
  const maxAttemptsReached = attempts >= MAX_ATTEMPTS;
  const canManagePayouts = activeEventUser?.status === EventUserStatus.owner || isSuperAdmin;
  const isValidUSAddress = useMemo(() => isAddressValid(registryShippingAddress) && isUSAddress(registryShippingAddress), [registryShippingAddress]);
  const shouldSkipAddressValidation = !!payoutAccountInfo && hasActiveStripeAccount(payoutAccountInfo.status);
  const handleMutationError = useCallback(
    (result: NormalizableGraphError, label: string) => {
      const normalized = normalizeGraphError(result);
      const errorMessage = translateGraphErrorCodeOrUnknown(normalized?.code);
      toast(errorMessage, { icon: <WarningTriangle />, duration: 10000 });
      telemetry.trackError(label, errorFromGraphError(result), {
        eventId,
        code: normalized?.code,
        nativeMessage: normalized?.nativeMessage,
      });
    },
    [eventId, toast, translateGraphErrorCodeOrUnknown, telemetry],
  );

  const [getPayoutInfo, { loading: fetchingPayoutInfo, called: hasFetchedPayoutInfo }] = useGetPayoutAccountInfoLazyQuery({
    batchMode: 'fast',
    // `no-cache` plus the `onCompleted` -> `setPayoutAccountInfo` funnel below is why passing an
    //   argument to `payoutAccount(options:)` is safe here: nothing this query returns is ever
    //   normalized into the Apollo cache, so a forced and an unforced read can't collide on a
    //   cache key. Restoring a caching fetchPolicy would reintroduce that hazard -- the two
    //   variants would share a key and the last writer would win. Don't "fix" this to cache-first.
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const pa = data.getRegistryEventData?.payoutAccount;
      if (!pa) {
        setPayoutAccountInfo(null);
        return;
      }
      setPayoutAccountInfo({
        status: pa.status as PayoutAccountInfo['status'],
        statusDetails: pa.statusDetails ?? '',
        currencyCode: pa.currencyCode,
        countryCode: pa.countryCode,
        onboardingUrl: pa.onboardingUrl ?? '',
        serviceDashboardUrl: pa.serviceDashboardUrl ?? '',
        externalAccountLast4: pa.externalAccountLast4 ?? null,
        balance: pa.balance ?? undefined,
        withdrawals: pa.withdrawals ?? [],
        defaultExternalAccount: mapRegistryExternalAccount(pa.defaultExternalAccount),
        standardPayoutFee: pa.standardPayoutFee ?? null,
        instantPayoutFee: pa.instantPayoutFee ?? null,
        payoutThrottleDelayMs: pa.payoutThrottleDelayMs ?? null,
        nextWithdrawalAvailableAt: pa.nextWithdrawalAvailableAt ?? null,
        payoutLimitInMinorUnits: pa.payoutLimitInMinorUnits ?? null,
      });
    },
  });
  const [createPayoutAccount, { loading: initPayoutAccountLoading }] = useCreateRegistryPayoutAccountMutation({
    onCompleted: data => {
      const createRegistryPayoutAccount = data.createRegistryPayoutAccount;
      if (createRegistryPayoutAccount.__typename === 'RegistryPayoutAccount') {
        const url = createRegistryPayoutAccount.onboardingUrl ?? '';
        if (url) openWindow(url, '_self');
      } else if (createRegistryPayoutAccount.__typename === 'RegistryErrorResult') {
        setHasKYCFailed(true);
        handleMutationError(createRegistryPayoutAccount, 'CreateRegistryPayoutAccount');
      }
    },
    onError: error => {
      setHasKYCFailed(true);
      addError(error, { context: 'CreditCardSetup.createPayoutAccount', eventId });
      telemetry.trackError('CreateRegistryPayoutAccount', error, { eventId });
      toast(translateGraphErrorCodeOrUnknown(undefined), { icon: <WarningTriangle />, duration: 10000 });
    },
  });

  const [getPayoutAccountOnboardingUrl, { loading: fetchingOnboardingUrl }] = useGetPayoutAccountOnboardingUrlLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const account = data.getRegistryEventData?.payoutAccount;
      const url = account?.onboardingUrl || account?.serviceDashboardUrl;
      if (url) {
        openWindow(url, '_self');
      } else {
        setHasKYCFailed(true);
        addError(new Error('No onboarding URL for existing payout account'), { context: 'CreditCardSetup.getPayoutAccountOnboardingUrl', eventId });
        toast(translateGraphErrorCodeOrUnknown(undefined), { icon: <WarningTriangle />, duration: 10000 });
      }
    },
    onError: error => {
      setHasKYCFailed(true);
      addError(error, { context: 'CreditCardSetup.getPayoutAccountOnboardingUrl', eventId });
      telemetry.trackError('GetPayoutAccountOnboardingUrl', error, { eventId });
      toast(translateGraphErrorCodeOrUnknown(undefined), { icon: <WarningTriangle />, duration: 10000 });
    },
  });

  const [initCashFundsHost, { loading: initKYCLaterLoading }] = useInitCashFundsHostFromRegistryMutation({
    onCompleted: data => {
      const result = data.initCashFundsHost;
      if (result.__typename === 'CashFundsHost') {
        getPayoutInfo({ variables: { eventId, options: { forceRefresh: false } } });
      } else if (result.__typename === 'CashFundGeneralError') {
        handleMutationError(result, 'InitCashFundsHostFromRegistry');
      }
    },
    onError: error => {
      addError(error, { context: 'CreditCardSetup.initCashFundsHost', eventId });
      telemetry.trackError('InitCashFundsHostFromRegistry', error, { eventId });
    },
  });

  const [syncRegistryCurrency] = useSetRegistryCurrencyMutation();
  const {
    countryCode: resolvedCountryCode,
    currencyCode: resolvedCurrencyCode,
    loading: resolvedLocaleLoading,
    supported: resolvedLocaleSupported,
  } = useResolvedCashFundLocale(registryCurrencyCode);
  const initPayoutAccountOrLocaleLoading = initPayoutAccountLoading || resolvedLocaleLoading || fetchingOnboardingUrl;
  const isLocaleSupported = resolvedLocaleSupported !== null;
  const isGetCashEquivalentSupported = useMemo(() => isCashEquivalentSupported({ payoutAccountInfo, registryCurrencyCode }), [payoutAccountInfo, registryCurrencyCode]);
  const initKYCOnboarding = useCallback(() => {
    const returnRoute = withWindow(w => appendStripeStatusMarker(w.location.pathname), '');
    if (payoutAccountInfo) {
      setHasKYCFailed(false);
      getPayoutAccountOnboardingUrl({ variables: { eventId, returnRoute } });
      return;
    }
    if (resolvedLocaleLoading) return;
    setHasKYCFailed(false);
    void createPayoutAccount({ variables: { eventId, returnRoute, payload: { currencyCode: resolvedCurrencyCode, countryCode: resolvedCountryCode } } });
  }, [eventId, createPayoutAccount, getPayoutAccountOnboardingUrl, payoutAccountInfo, resolvedCountryCode, resolvedCurrencyCode, resolvedLocaleLoading]);
  const initKYCLaterOnboarding = useCallback(
    async (firstName: string, lastName: string, currencyCode: string, countryCode: string): Promise<boolean> => {
      try {
        const result = await initCashFundsHost({
          variables: {
            input: {
              eventId,
              firstName,
              lastName,
              currencyCode,
              address: { countryCode },
            },
          },
        });
        const success = result.data?.initCashFundsHost.__typename === 'CashFundsHost';
        if (success && registryCurrencyCode && currencyCode !== registryCurrencyCode) {
          await syncRegistryCurrency({
            variables: { eventId, payload: { code: currencyCode } },
            refetchQueries: () => ['GetAdminRegistryData', 'PublicShopRoot'],
          });
        }
        return success;
      } catch (error) {
        addError(error, { context: 'CreditCardSetup.initKYCLaterOnboarding', eventId });
        return false;
      }
    },
    [eventId, initCashFundsHost, registryCurrencyCode, syncRegistryCurrency],
  );
  const initKYCOnboardingFromRefund = useCallback(
    (returnRoute: string) => {
      const routeWithMarker = appendStripeStatusMarker(returnRoute);
      if (payoutAccountInfo) {
        setHasKYCFailed(false);
        getPayoutAccountOnboardingUrl({ variables: { eventId, returnRoute: routeWithMarker } });
        return;
      }
      if (resolvedLocaleLoading) return;
      setHasKYCFailed(false);
      void createPayoutAccount({ variables: { eventId, returnRoute: routeWithMarker, payload: { currencyCode: resolvedCurrencyCode, countryCode: resolvedCountryCode } } });
    },
    [eventId, createPayoutAccount, getPayoutAccountOnboardingUrl, payoutAccountInfo, resolvedCountryCode, resolvedCurrencyCode, resolvedLocaleLoading],
  );
  // `forceRefresh` makes the backend skip its cached copy and read the payout account live from
  //   Stripe. That is a real round trip across two services, so it stays opt-in per call: only the
  //   bank-account dialog's close handler passes it. The pending-retry loop and the mount fetch
  //   below deliberately call this unforced.
  const fetchPayoutInfo = useCallback(
    (opts?: { forceRefresh?: boolean }) => getPayoutInfo({ variables: { eventId, options: { forceRefresh: !!opts?.forceRefresh } } }),
    [eventId, getPayoutInfo],
  );

  const [setupCreditCard, setSetupCreditCard] = useManagedSearchParam('setupCreditCard');
  const [postKycAction, setPostKycAction] = useManagedSearchParam('postKycAction');
  const [, setAddCashFund] = useManagedSearchParam('addCashFund');

  // Promotes the postKycAction URL flag set by a caller (e.g. the bank-account banner) into the
  // actual downstream URL state once KYC-later submit succeeds — keeps the chain URL-driven and
  // matches the same pattern setupCreditCard uses.
  const handleDialogSubmissionSuccess = useCallback(() => {
    if (postKycAction === 'addCashFund') {
      setPostKycAction(null);
      setAddCashFund('true');
    }
  }, [postKycAction, setPostKycAction, setAddCashFund]);

  const handleDialogClose = useCallback(() => {
    setSetupCreditCard(null);
    setPostKycAction(null);
  }, [setSetupCreditCard, setPostKycAction]);
  const [checkStripeStatus, setCheckStripeStatus] = useManagedSearchParam(CHECK_STRIPE_STATUS_PARAM);
  const [preloadedOrderIdForRefund] = useManagedSearchParam('preloadedOrderIdForRefund');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isBankConnected = !!payoutAccountInfo && canWithdrawFunds(payoutAccountInfo.status);

  const {
    handleTransfer,
    loadingPayoutRequest,
    isOpenPayoutDialog,
    onOpenPayoutDialog,
    onClosePayoutDialog,
    payoutStatus,
    dialogStep,
    amountInMinorUnits,
    onAmountChange,
    onContinueToMethod,
    onContinueToAmount,
    onBackToAmount,
    selectedMethod,
    onMethodChange,
  } = usePayoutDialogController(fetchPayoutInfo, payoutAccountInfo, eventId as string);

  // const dialogRef = useOuterClick(onToggle, isOpen);
  // State will be handle by a query param, if this query param is present the modal will close and open.
  useEffect(() => {
    if (setupCreditCard) {
      onOpen();
    } else {
      onClose();
    }
  }, [onOpen, setupCreditCard, onClose]);

  usePostStripeReturnEffect({
    checkStripeStatus,
    fetchingPayoutInfo,
    payoutAccountInfo,
    preloadedOrderIdForRefund,
    setSetupCreditCard,
    setCheckStripeStatus,
  });

  // we don't clear out historical failures, so we only emphasize the last withdrawal
  const hasFailedWithdrawals = useMemo(() => payoutAccountInfo?.withdrawals.at(-1)?.status === RegistryPayoutAccountWithdrawalStatus.failed, [payoutAccountInfo]);
  const hasPendingWithdrawals = useMemo(
    () => payoutAccountInfo?.withdrawals.some(withdrawal => withdrawal.status === RegistryPayoutAccountWithdrawalStatus.pending) ?? false,
    [payoutAccountInfo],
  );

  const prevHasPendingWithdrawals = usePreviousValue(hasPendingWithdrawals);
  const [hasDoneWithdrawal, setHasDoneWithdrawal] = useState(false);
  useEffect(() => {
    // a withdrawal is pending, so clear done withdrawal
    if (hasPendingWithdrawals) {
      setHasDoneWithdrawal(false);
      return;
    }
    // previous withdrawal has been completed without error
    if (!hasPendingWithdrawals && prevHasPendingWithdrawals && !hasFailedWithdrawals) {
      setHasDoneWithdrawal(true);
      return;
    }
  }, [hasFailedWithdrawals, prevHasPendingWithdrawals, hasPendingWithdrawals, setHasDoneWithdrawal]);
  useEffect(() => {
    return withWindow(
      global => {
        let timeout: number;
        if (payoutAccountInfo && (payoutAccountInfo.status === 'pending' || hasPendingWithdrawals)) {
          if (attempts <= MAX_ATTEMPTS) {
            timeout = global.setTimeout(() => {
              setAttempts(prevValue => prevValue + 1);
              fetchPayoutInfo();
            }, 10_000);
          }
        }

        return () => {
          if (timeout) {
            global.clearTimeout(timeout);
          }
        };
      },
      () => {},
    );
  }, [payoutAccountInfo, fetchPayoutInfo, eventId, attempts, setAttempts, hasPendingWithdrawals]);

  useEffect(() => {
    fetchPayoutInfo();
  }, [fetchPayoutInfo, eventId]);
  return (
    <Provider
      value={{
        hasDoneWithdrawal,
        hasFailedWithdrawals,
        hasPendingWithdrawals,
        canManagePayouts,
        setSetupCreditCard,
        fetchingPayoutInfo,
        payoutInfoChecked: hasFetchedPayoutInfo && !fetchingPayoutInfo,
        payoutAccountInfo,
        maxAttemptsReached,
        initKYCOnboarding,
        kycReady: !resolvedLocaleLoading,
        isLocaleSupported,
        isGetCashEquivalentSupported,
        resolvedCashFundCountryCode: resolvedCountryCode,
        resolvedCashFundCurrencyCode: resolvedCurrencyCode,
        hasKYCFailed,
        fetchPayoutInfo,
        onOpenPayoutDialog,
        initKYCOnboardingFromRefund,
        initKYCLaterOnboarding,
        initPayoutAccountLoading: initPayoutAccountOrLocaleLoading,
        initKYCLaterLoading,
      }}
    >
      {children}
      <CreditCardSetupInitiatorDialog
        payoutAccountInfo={payoutAccountInfo}
        canManagePayouts={canManagePayouts}
        onClose={handleDialogClose}
        onSubmissionSuccess={handleDialogSubmissionSuccess}
        isOpen={isOpen}
        initKYCOnboarding={initKYCOnboarding}
        initPayoutAccountLoading={initPayoutAccountOrLocaleLoading}
        initKYCLaterOnboarding={initKYCLaterOnboarding}
        initKYCLaterLoading={initKYCLaterLoading}
        maxAttemptsReached={maxAttemptsReached}
        isValidUSAddress={isValidUSAddress}
        isLocaleSupported={isLocaleSupported}
        shouldSkipAddressValidation={shouldSkipAddressValidation}
        handleInvalidShippingAddress={handleInvalidShippingAddress}
        handleChangeCurrency={handleChangeCurrency}
        registryCurrencyCode={registryCurrencyCode}
        shippingCountryCode={registryShippingAddress?.countryCode ?? undefined}
        forceFullKyc={setupCreditCard === 'fullKyc'}
      />
      {payoutAccountInfo && (
        <PayoutDialog
          isOpen={isOpenPayoutDialog}
          onClose={onClosePayoutDialog}
          payoutAccountInfo={payoutAccountInfo}
          dialogStep={dialogStep}
          amountInMinorUnits={amountInMinorUnits}
          onAmountChange={onAmountChange}
          onContinue={onContinueToMethod}
          onBack={onBackToAmount}
          selectedMethod={selectedMethod}
          onMethodChange={onMethodChange}
          onTransfer={handleTransfer}
          loadingTransfer={loadingPayoutRequest}
          status={payoutStatus}
          onConnectBank={
            isBankConnected
              ? onContinueToAmount
              : () => {
                  onClosePayoutDialog();
                  const redirectUrl = payoutAccountInfo.onboardingUrl || payoutAccountInfo.serviceDashboardUrl;
                  if (redirectUrl) openWindow(redirectUrl, '_self');
                  else initKYCOnboarding();
                }
          }
          onBankPromoViewed={partner => registryTelemetry.transferDialogBankPromoViewed(partner)}
          onBankPromoInteracted={(cta, partner) => registryTelemetry.transferDialogBankPromoInteracted(cta, partner)}
        />
      )}
    </Provider>
  );
};

export { CreditCardSetupProvider };
